import { postRequest, getRequest } from './apiService';
import { MarketViewDTO } from '../components/products/positive-ev/components/market-view/market-view-service';
import { PositiveEVRow } from '../components/products/positive-ev/types/positive-ev.types';
import type { ArbitrageRow } from '../components/products/arbitrage/types/arbitrage.types';
import { isDisplayableHeroArb } from '../components/products/arbitrage/services/hero-arb-ticket';

export interface ShareResponse {
  share_id?: string;
  limit_reached?: boolean;
}

/**
 * Generates a shareable URL for a market view (Positive EV)
 * @param marketViewDTO - The market view data to share
 * @returns Promise with the shareId
 */
export const generateShareUrl = async (marketViewDTO: MarketViewDTO): Promise<ShareResponse | null> => {
  const url = '/api/v1/share';
  try {
    const response = await postRequest(url, marketViewDTO);
    return response;
  } catch (error) {
    console.error('Failed to generate share URL', error);
    return null;
  }
};

/**
 * Fetches shared data by shareId (Positive EV)
 * @param shareId - The share ID from the URL
 * @returns Promise with the shared rows data
 */
export const getSharedData = async (shareId: string): Promise<{ rows: PositiveEVRow[], screen_name?: string, shared_by_id?: number, expired?: boolean } | null> => {
  const url = `/api/v1/share/${shareId}`;
  try {
    const response = await getRequest(url);
    return response?.data || null;
  } catch (error) {
    console.error('Failed to fetch shared data', error);
    return null;
  }
};

/**
 * Generates a shareable URL for an arbitrage bet
 * @param marketViewDTO - The market view data to share
 * @returns Promise with the shareId
 */
export const generateArbShareUrl = async (marketViewDTO: MarketViewDTO): Promise<ShareResponse | null> => {
  const url = '/api/v1/share/arb';
  try {
    const response = await postRequest(url, marketViewDTO);
    return response;
  } catch (error) {
    console.error('Failed to generate arb share URL', error);
    return null;
  }
};

/**
 * Fetches shared arb data by shareId
 * @param shareId - The share ID from the URL
 * @returns Promise with the shared arb row data
 */
export const getArbSharedData = async (shareId: string): Promise<{ row: ArbitrageRow, screen_name?: string, shared_by_id?: number, expired?: boolean } | null> => {
  const url = `/api/v1/share/arb/${shareId}`;
  try {
    const response = await getRequest(url);
    return response?.data || null;
  } catch (error) {
    console.error('Failed to fetch arb shared data', error);
    return null;
  }
};

/**
 * Generates a shareable URL for a parlay. The frontend encodes the parlay
 * legs into a base64-url payload (see parlay-share-utils.ts), and the
 * backend persists that payload + sharer attribution and returns a short
 * shareId that becomes the URL slug.
 */
export const generateParlayShareUrl = async (
  encodedPayload: string,
  reshare = false
): Promise<ShareResponse | null> => {
  try {
    const response = await postRequest('/api/v1/share/parlay', {
      payload: encodedPayload,
      reshare,
    });
    return response;
  } catch (error) {
    console.error('Failed to generate parlay share URL', error);
    return null;
  }
};

/**
 * Fetches shared parlay data by shareId.
 * Returns the encoded payload (decode with decodeParlayFromShare) plus
 * sharer attribution (screenName, sharedById) for the share landing page.
 */
export const getParlaySharedData = async (
  shareId: string
): Promise<{ payload: string; screen_name?: string; shared_by_id?: number; expired?: boolean } | null> => {
  const url = `/api/v1/share/parlay/${shareId}`;
  try {
    const response = await getRequest(url);
    return response?.data || null;
  } catch (error) {
    console.error('Failed to fetch shared parlay data', error);
    return null;
  }
};

// ─── Snapshot shares: trader signals, sharp-money bets, run pools ───────────

interface SnapshotShareCardFields {
  event?: string | null;
  description?: string | null;
  bet_type?: string | null;
  market_type?: string | null;
  book_key?: string | null;
  book_odds?: number | null;
  edge_percent?: number | null;
  event_date?: string | null;
}

const generateSnapshotShareUrl = async (
  path: string,
  encodedPayload: string,
  card: SnapshotShareCardFields,
  reshare = false
): Promise<ShareResponse | null> => {
  try {
    return await postRequest(path, { payload: encodedPayload, reshare, ...card });
  } catch (error) {
    console.error(`Failed to generate share URL via ${path}`, error);
    return null;
  }
};

/**
 * Shares a Traders (prediction-market whale) signal. The row is frozen into
 * the payload — there's no durable market pointer to re-resolve later — and
 * the card fields ride along for the feed embed and OG unfurls.
 */
export const generateTraderShareUrl = (
  encodedPayload: string,
  card: SnapshotShareCardFields,
  reshare = false
): Promise<ShareResponse | null> =>
  generateSnapshotShareUrl('/api/v1/share/trader', encodedPayload, card, reshare);

/** Shares a Sharp Money bet — same snapshot mechanics as trader shares. */
export const generateSharpShareUrl = (
  encodedPayload: string,
  card: SnapshotShareCardFields,
  reshare = false
): Promise<ShareResponse | null> =>
  generateSnapshotShareUrl('/api/v1/share/sharp', encodedPayload, card, reshare);

/**
 * Shares a 13 Run Pool by its invite code. The server owns the pool row, so
 * it builds the snapshot itself; re-sharing the same pool returns the same
 * share (idempotent), refreshed with the current player count.
 */
export const generateRunPoolShareUrl = async (
  shareCode: string,
  reshare = false
): Promise<ShareResponse | null> => {
  try {
    return await postRequest('/api/v1/share/run-pool', { share_code: shareCode, reshare });
  } catch (error) {
    console.error('Failed to generate run pool share URL', error);
    return null;
  }
};

export interface SnapshotSharedData {
  share_id: string;
  share_type: string;
  payload: string;
  shared_by_id?: number | null;
  screen_name?: string | null;
}

/** Fetches a trader / sharp / run-pool share for its landing page. */
export const getSnapshotSharedData = async (
  type: 'trader' | 'sharp' | 'run-pool',
  shareId: string
): Promise<SnapshotSharedData | null> => {
  try {
    const response = await getRequest(`/api/v1/share/${type}/${encodeURIComponent(shareId)}`);
    return response?.data || null;
  } catch (error) {
    console.error(`Failed to fetch shared ${type} data`, error);
    return null;
  }
};

// ─── Web Share / Clipboard utility ──────────────────────────────────────────

interface WebShareOptions {
  url: string;
  title?: string;
  text?: string;
}

/**
 * Attempts the Web Share API on mobile, falling back to clipboard copy.
 * Returns true if the content was shared/copied successfully.
 */
export const webShareOrCopy = async ({
  url,
  title = 'Check this out from BookieBrawler',
}: WebShareOptions): Promise<boolean> => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Share just the link (with a short title). The link's own preview card —
  // its OG title and image — is all the context we want; no canned "check out
  // this bet" message and no separately attached image.
  if (isMobile && navigator.share) {
    try {
      await navigator.share({ title, url });
      return true;
    } catch (err: any) {
      if (err.name === 'AbortError') return false;
      // fall through to clipboard
    }
  }

  return copyToClipboard(url);
};

/**
 * Copies text to the clipboard with broad browser/OS support.
 * Returns true on success.
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // fall through
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    Object.assign(textArea.style, {
      position: 'fixed', top: '0', left: '0',
      width: '2em', height: '2em', padding: '0',
      border: 'none', outline: 'none', boxShadow: 'none',
      background: 'transparent', opacity: '0',
    });
    textArea.setAttribute('readonly', '');
    textArea.setAttribute('aria-hidden', 'true');
    document.body.appendChild(textArea);

    if (/ipad|ipod|iphone/i.test(navigator.userAgent)) {
      const range = document.createRange();
      range.selectNodeContents(textArea);
      const selection = window.getSelection();
      if (selection) {
        selection.removeAllRanges();
        selection.addRange(range);
      }
      textArea.setSelectionRange(0, 999999);
    } else {
      textArea.select();
    }

    const ok = document.execCommand('copy');
    document.body.removeChild(textArea);
    return ok;
  } catch {
    return false;
  }
};

// ─── Free Bet of the Day ────────────────────────────────────────────────────

const FREE_BET_CACHE_KEY = 'bb_free_bet_of_day';
const FREE_BET_DATE_KEY = 'bb_free_bet_date';
const FEATURED_ARB_CACHE_KEY = 'bb_featured_hero_arb';
const FEATURED_ARB_CACHE_TTL_MS = 10 * 60 * 1000;
const MIN_FEATURED_ARB_PERCENT = 0.5;
const ARB_FALLBACK = '/arbitrage';

export interface FreeBetOfTheDay {
  shareId: string;
  shareUrl: string;
  screenName?: string;
  expired?: boolean;
}

export interface FeaturedHeroArb {
  row: ArbitrageRow;
  shareUrl: string;
}

/**
 * Gets the "Free Bet of the Day" - the latest arb bet shared by the
 * BookieBrawler team. Cached in localStorage so it only hits the API once per day.
 */
export const getFreeBetOfTheDay = async (): Promise<FreeBetOfTheDay | null> => {
  const today = new Date().toISOString().slice(0, 10); // "YYYY-MM-DD"

  // Check localStorage cache - only reuse if same calendar day
  const cachedDate = localStorage.getItem(FREE_BET_DATE_KEY);
  const cachedData = localStorage.getItem(FREE_BET_CACHE_KEY);

  if (cachedDate === today && cachedData) {
    try {
      return JSON.parse(cachedData) as FreeBetOfTheDay;
    } catch {
      // corrupt cache, fall through to fetch
    }
  }

  // Fetch from API
  try {
    const response = await getRequest('/api/v1/share/arb/free-bet');
    const data = response?.data;

    if (!data || !data.share_id) {
      return null;
    }

    const freeBet: FreeBetOfTheDay = {
      shareId: data.share_id,
      shareUrl: `/arbitrage/share/${data.share_id}`,
      screenName: data.screen_name,
      expired: data.expired || false,
    };

    // Cache for the rest of today
    localStorage.setItem(FREE_BET_CACHE_KEY, JSON.stringify(freeBet));
    localStorage.setItem(FREE_BET_DATE_KEY, today);

    return freeBet;
  } catch (error) {
    console.error('Failed to fetch free bet of the day', error);
    return null;
  }
};

const unwrapArbRows = (raw: any, fallbackCategory?: ArbitrageRow['category']): ArbitrageRow[] => {
  let rowsRaw = raw?.data ?? raw ?? [];
  if (rowsRaw && !Array.isArray(rowsRaw)) {
    rowsRaw = rowsRaw.data ?? rowsRaw.arbs ?? rowsRaw.results ?? rowsRaw.rows ?? [];
  }
  const rows: ArbitrageRow[] = Array.isArray(rowsRaw) ? rowsRaw : [];
  return rows.map((row) => ({
    ...row,
    category: row.category ?? fallbackCategory,
  }));
};

const fetchArbRows = async (path: string, fallbackCategory?: ArbitrageRow['category']): Promise<ArbitrageRow[]> => {
  try {
    const response = await getRequest(path);
    return unwrapArbRows(response, fallbackCategory);
  } catch {
    return [];
  }
};

const arbEdge = (row: ArbitrageRow): number =>
  Number.isFinite(row?.arb_percent) ? Number(row.arb_percent) : 0;

const shareUrlForArb = async (row: ArbitrageRow): Promise<string> => {
  if (!row.odd_id && !row.raw_market_type) return ARB_FALLBACK;
  const dto: MarketViewDTO = {
    odd_id: row.odd_id || '',
    market_type: row.raw_market_type || '',
    description: row.description || '',
    bet_type: row.bet_type || '',
    event: row.event,
  };
  const shareResp = await generateArbShareUrl(dto);
  return shareResp?.share_id ? `/arbitrage/share/${shareResp.share_id}` : ARB_FALLBACK;
};

/**
 * Hero ticket source: scan the current arb board and show a real displayable
 * ticket when one exists. The hero handles a shared-ticket fallback so the
 * first fold can sell with a concrete bet instead of a generic board panel.
 */
export const getFeaturedHeroArb = async (): Promise<FeaturedHeroArb | null> => {
  try {
    const cached = localStorage.getItem(FEATURED_ARB_CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached) as FeaturedHeroArb & { cachedAt?: number };
      if (parsed?.row && parsed.cachedAt && Date.now() - parsed.cachedAt < FEATURED_ARB_CACHE_TTL_MS) {
        return parsed;
      }
    }
  } catch {
    localStorage.removeItem(FEATURED_ARB_CACHE_KEY);
  }

  const [sports, predictionMarkets] = await Promise.all([
    fetchArbRows('/v1/odds/arbs', 'SPORTS'),
    fetchArbRows('/v1/odds/arbs/prediction-markets', 'PREDICTION_MARKET'),
  ]);
  const best = [...sports, ...predictionMarkets]
    .filter((row) => arbEdge(row) >= MIN_FEATURED_ARB_PERCENT)
    .filter((row) => isDisplayableHeroArb(row, MIN_FEATURED_ARB_PERCENT))
    .sort((a, b) => arbEdge(b) - arbEdge(a))[0];

  if (!best) return null;

  const featured: FeaturedHeroArb & { cachedAt: number } = {
    row: best,
    shareUrl: await shareUrlForArb(best),
    cachedAt: Date.now(),
  };
  localStorage.setItem(FEATURED_ARB_CACHE_KEY, JSON.stringify(featured));
  return featured;
};

/**
 * Grabs the highest-scored arb currently available, generates a share link
 * for it, and returns the URL path. Falls back to the backend free-bet
 * endpoint first (cached), then manually picks from live arbs.
 *
 * Result is cached in localStorage for the calendar day so repeat visits
 * don't re-create shares.
 */
export const generateBetOfTheDayLink = async (): Promise<string> => {
  const freeBet = await getFreeBetOfTheDay();
  if (freeBet?.shareUrl && !freeBet.expired) {
    return freeBet.shareUrl;
  }

  try {
    const response = await getRequest('/v1/odds/arbs');
    let raw: any = response?.data ?? response ?? [];
    if (raw && !Array.isArray(raw)) {
      raw = raw.data ?? raw.arbs ?? raw.results ?? raw.rows ?? [];
    }
    const arbs: ArbitrageRow[] = Array.isArray(raw) ? raw : [];
    const positiveArbs = arbs.filter(
      (arb) => Number.isFinite(arb.arb_percent) && arb.arb_percent > 0
    );
    if (positiveArbs.length === 0) return ARB_FALLBACK;

    const best = positiveArbs.reduce((a, b) =>
      b.arb_percent > a.arb_percent ? b : a
    );

    const dto: MarketViewDTO = {
      odd_id: best.odd_id || '',
      market_type: best.raw_market_type || '',
      description: best.description || '',
      bet_type: best.bet_type || '',
      event: best.event,
    };

    const shareResp = await generateArbShareUrl(dto);
    if (!shareResp?.share_id) return ARB_FALLBACK;

    const shareUrl = `/arbitrage/share/${shareResp.share_id}`;

    const today = new Date().toISOString().slice(0, 10);
    const cached: FreeBetOfTheDay = { shareId: shareResp.share_id, shareUrl };
    localStorage.setItem(FREE_BET_CACHE_KEY, JSON.stringify(cached));
    localStorage.setItem(FREE_BET_DATE_KEY, today);

    return shareUrl;
  } catch (error) {
    console.error('Failed to generate bet of the day link', error);
    return ARB_FALLBACK;
  }
};
